import { useCallback, useContext, useEffect, useState } from "react";
import { IResourcesProps } from "./Resources.interface";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import Accordion from "../../components/Accordion/Accordion";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { useResources } from "../../shared/hooks/useResources";
import { DEFAULT_LANGUAGE } from "../../shared/constants";
import { IResource, IResourceItem, ISubsection } from "../../shared/interfaces/resources.interface";
import Select from "../../components/Forms/Select/Select";
import { useCountries } from "../../shared/hooks/useCountries";
import { ICountry } from "../../shared/interfaces/countries.interface";

function Resources(props: IResourcesProps) {
    const [resources, setResources] = useState<IResource[]>();
    const [countries, setCountries] = useState<ICountry[]>();
    const [currentCountry, setCurrentCountry] = useState<string>();

    const { setIsLoading } = useContext(LoadingContext);
    const { translate, lang } = useContext(TranslationContext);
    const {
        isLoading,
        data,
        refetch: getResources
    } = useResources(lang ?? DEFAULT_LANGUAGE, currentCountry, {
        enabled: false
    });
    const { isLoading: isLoadingCountries, data: dataCountries } = useCountries(
        lang ?? DEFAULT_LANGUAGE
    );

    useEffect(() => {
        if ((currentCountry && props.national) || !props.national) getResources();
    }, [currentCountry]);

    useEffect(() => {
        if (isLoading || !data?.success) return;
        setResources(data.data?.resources);
    }, [data]);

    useEffect(() => {
        if (isLoadingCountries || !dataCountries?.success) return;
        setCountries(dataCountries.data);
    }, [dataCountries]);

    useEffect(() => {
        setIsLoading(isLoading || isLoadingCountries);
    }, [isLoading, isLoadingCountries]);

    const getBody = useCallback((item: IResource) => {
        const getItem = (item: IResourceItem, i: number) => {
            return (
                    <li className="py-2" key={i}>
                        <a
                            rel="noreferrer"
                            target="_blank"
                            href={item.link.href}
                            className="d-flex justify-content-between align-items-center text-h5-bd color-neutrals-black">
                            {item.link.text}
                            <i
                                className={`icon icon-24 icon-${item.type === "LINK" ? "ungroup" : "Phone"}`}></i>
                        </a>
                        {item.description && <p className="mb-0">{item.description}</p>}
                    </li>
            );
        };

        return (
            <>
                {item.subsections.map((subsection, i) => {
                    if (subsection.subtitle) {
                        return (
                            <div key={i} className="subsection">
                                <p className="text-bd2-rg color-neutrals-black mt-2 mb-n1">{subsection.subtitle}</p>
                                <ul>{subsection.resources.map((item, i) => getItem(item, i))}</ul>
                            </div>
                        );
                    }else {
                        return <ul key={i} className="subsection">{subsection.resources.map((item, i) => getItem(item, i))}</ul>
                    }
                })}
            </>
        );
    }, []);

    return (
        <div className="container-fluid gx-2 pt-2 pb-5">
            <div className="resources">
                <h1 className="text-h3-md mb-12">
                    {props.national
                        ? translate(
                              "common.national_resources_services",
                              "National Resources and Services"
                          )
                        : translate(
                              "common.international_resources_services",
                              "International Resources and Services"
                          )}
                </h1>
                {props.national && (
                    <div className="mb-12">
                        <Select
                            filled
                            placeholder="Country"
                            value={currentCountry}
                            options={
                                countries?.map((c) => ({ value: c.isoCode, label: c.name })) ?? []
                            }
                            onChange={(e) => setCurrentCountry(e.value)}
                        />
                    </div>
                )}
                <div className="resources_content d-flex flex-column gap-22 color-neutrals-black">
                    <Accordion
                        items={
                            resources?.map((r) => ({
                                title: r.title,
                                body: getBody(r)
                            })) ?? []
                        }
                    />
                </div>
            </div>
        </div>
    );
}

export default Resources;
